import { Plus, Eye } from "lucide-react";
import { formatPrice, applyDiscount } from "@/lib/api";
import { catLabel } from "@/lib/taxonomy";
import { optImg } from "@/lib/img";
import { useCart } from "@/context/CartContext";
import { useProductModal } from "@/context/ProductModalContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const { openProduct } = useProductModal();
  const { settings } = useSettings();

  const hasVariants = (product.variants || []).some((v) => v && v.name);
  const discount = Number(product.discount) || 0;
  const baseFrom = hasVariants
    ? Math.min(...product.variants.filter((v) => v && v.price).map((v) => v.price).concat(product.price))
    : product.price;
  const finalFrom = applyDiscount(baseFrom, discount);

  const add = (e) => {
    e.stopPropagation();
    if (hasVariants) {
      openProduct(product);
      return;
    }
    addItem(product, 1);
    toast.success("Agregado al carrito", { description: product.name });
  };

  return (
    <article
      data-testid={`product-card-${product.id}`}
      onClick={() => openProduct(product)}
      className="group relative bg-white rounded-3xl border border-[#E3E7E2] overflow-hidden card-shadow hover:-translate-y-1 transition-transform duration-300 cursor-pointer animate-float-up"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
    >
      <div className="relative aspect-square overflow-hidden bg-white">
        <img
          src={optImg(product.image_url, 500)}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-2 left-2 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-sage/90 text-charcoal backdrop-blur-sm">
          {catLabel(product.category)}
        </span>
        {!product.in_stock && (
          <span className="absolute top-2 right-2 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-charcoal/80 text-white">
            Agotado
          </span>
        )}
        {hasVariants && product.in_stock && (
          <span className="absolute top-2 right-2 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-teal/90 text-charcoal">
            Opciones
          </span>
        )}
        {discount > 0 && (
          <span className="absolute bottom-2 left-2 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-coral text-white shadow">
            -{discount}%
          </span>
        )}
        <button
          data-testid={`view-detail-button-${product.id}`}
          onClick={(e) => { e.stopPropagation(); openProduct(product); }}
          className="absolute bottom-2 right-2 grid place-items-center w-9 h-9 rounded-full bg-white/90 text-charcoal opacity-0 group-hover:opacity-100 transition-opacity hover:bg-teal"
          aria-label="Ver detalle"
        >
          <Eye className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3 sm:p-4">
        <h3 className="font-semibold text-sm sm:text-base text-charcoal line-clamp-2 leading-snug min-h-[2.6em] uppercase">
          {product.name}
        </h3>
        {product.presentation && (
          <p className="text-xs text-muted-moss mt-0.5">{product.presentation}</p>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="font-display text-lg font-bold text-coral leading-none">
            {hasVariants && <span className="block text-[10px] font-semibold text-muted-moss uppercase tracking-wide">Desde</span>}
            <span className="flex items-baseline gap-1.5">
              {formatPrice(finalFrom, settings.currency)}
              {discount > 0 && (
                <span className="text-xs font-medium text-muted-moss line-through">{formatPrice(baseFrom, settings.currency)}</span>
              )}
            </span>
          </span>
          <button
            data-testid={`add-to-cart-button-${product.id}`}
            onClick={add}
            disabled={!product.in_stock}
            className="grid place-items-center w-9 h-9 rounded-2xl bg-coral text-white hover:bg-coral-dark active:scale-90 transition-all disabled:opacity-40"
            aria-label={hasVariants ? "Elegir opciones" : "Agregar al carrito"}
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>
    </article>
  );
};
